// src/pages/Admin/Productions/creditReach.ts
// Why a production's credits do or do not appear on a player page.
//
// The server selects credits whose production is joined to the content through
// ProductionWork *and* whose production is PUBLIC. Every way of failing that is silent:
// the credits sit there in the admin screen looking entered, and the player shows an
// empty credits section with nothing to explain it.
//
// Kept apart from the component so the rule can be tested as a rule.

import type { Production } from '../../../types/studio';

export type ReachState = 'live' | 'draft' | 'no-works' | 'no-credits';

export interface CreditReach {
  state: ReachState;
  /** What is true right now, in a sentence an admin can act on. */
  message: string;
  /** False whenever the credits on this production reach nobody. */
  live: boolean;
}

export function creditReach(production: Production): CreditReach {
  const works = production.works?.length ?? 0;
  const credits = production.creditCount ?? 0;

  // Order matters: report the step that is actually blocking, not the first one that
  // happens to be empty. Visibility comes first because it is the one that silently
  // takes *already working* credits off every player page the moment it changes.
  if (production.visibility !== 'PUBLIC') {
    return {
      state: 'draft',
      live: false,
      message:
        'This production is a draft. Its credits appear nowhere on the site until visibility is set to Public.',
    };
  }
  if (works === 0) {
    return {
      state: 'no-works',
      live: false,
      message:
        'No film is attached to this production, so its credits have nothing to appear under. Attach the film below.',
    };
  }
  if (credits === 0) {
    return {
      state: 'no-credits',
      live: false,
      message: 'No credits have been entered yet, so the credits section on the player is empty.',
    };
  }
  return {
    state: 'live',
    live: true,
    message:
      works === 1
        ? 'These credits appear on the attached film.'
        : `These credits appear on all ${works} attached items.`,
  };
}
